import { useSyncExternalStore } from "react";
import { demoApi, problemMessage } from "@/api/client";
import type { VendorTax } from "@/api/types";

type Snapshot = {
  state: "idle" | "loading" | "ready" | "error";
  rows: ReadonlyMap<string, VendorTax>;
  error: string | null;
};
const empty: Snapshot = { state: "idle", rows: new Map(), error: null };
let snapshot = empty;
let controller: AbortController | null = null;
const listeners = new Set<() => void>();
const getSnapshot = () => snapshot;
function publish(next: Snapshot): void {
  snapshot = next;
  listeners.forEach((listener) => listener());
}

// This store accepts server tax-state DTOs only: no input or write body is cached.
export function updateVendorTax(tax: VendorTax): void {
  const rows = new Map(snapshot.rows);
  rows.set(tax.vendorId, tax);
  publish({ ...snapshot, rows });
}

function load(): void {
  if (snapshot.state !== "idle" || listeners.size === 0) return;
  const request = new AbortController();
  controller = request;
  publish({ ...snapshot, state: "loading", error: null });
  void demoApi.getVendorTaxList(request.signal).then((response) => {
    if (request.signal.aborted) return;
    const rows = new Map(response.vendors.map((tax) => [tax.vendorId, tax] as const));
    // A save completed during the list request: its newer answer wins.
    snapshot.rows.forEach((tax, id) => rows.set(id, tax));
    publish({ state: "ready", rows, error: null });
  }).catch((err: unknown) => {
    if (!request.signal.aborted)
      publish({ ...snapshot, state: "error", error: problemMessage(err, "Could not load tax info.") });
  });
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  queueMicrotask(load);
  return () => {
    listeners.delete(listener);
    // StrictMode's immediate resubscribe shares the request. Leaving the page or
    // signing out drops the masked profiles, so another session cannot reuse them.
    queueMicrotask(() => {
      if (listeners.size !== 0) return;
      controller?.abort();
      controller = null;
      snapshot = empty;
    });
  };
}

export function retryVendorTaxes(): void {
  if (snapshot.state !== "error") return;
  publish({ ...snapshot, state: "idle", error: null });
  load();
}

export function useVendorTaxStore(): Snapshot {
  return useSyncExternalStore(subscribe, getSnapshot, () => empty);
}
